import { Box, Skeleton, Text, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';
import { DataTape } from 'types/manualDataTapeUpload';

import { extractDataTapeMapFromRequiredDataTapes } from './extractDataTapeMapFromRequiredDataTapes';
import { uploadTapeForLenderVerification } from './uploadTapeForLenderVerification';
import { InfoIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetRequiredDataTapesForVerificationQuery } from 'packages/manualDataTapeUploads/requests/requests.gql.generated';
import { DataTapeFormatModal } from 'packages/manualDataTapeUploads/ui/DataTapeFormatModal';
import { UploadDataTapeForm } from 'packages/manualDataTapeUploads/ui/UploadDataTapeForm';
import { useSandboxMode } from '~/contexts/SandboxModeProvider';
import { getAuthTokenOrThrow } from '~/contexts/UserAuthProvider';

type UploadFormProps = {
  tape: DataTape;
  uploadFile: (file: File, tape: DataTape) => Promise<void>;
};
const UploadForm = ({ tape, uploadFile }: UploadFormProps) => {
  const [isFormatModalOpen, setIsModalFormatOpen] = useState(false);
  return (
    <Box>
      <Box float="right" padding="12px">
        <InfoIconButton
          ariaLabel="Data Tape Format"
          label="Data Tape Format"
          onClick={() => setIsModalFormatOpen(true)}
          fontSize="3xl"
          colorScheme="blue"
        />
      </Box>
      <DataTapeFormatModal
        isOpen={isFormatModalOpen}
        onClose={() => setIsModalFormatOpen(false)}
        dataTape={tape}
      />
      <Text>{commonUtils.toTitleCase(tape.artifactSlug)}</Text>
      <UploadDataTapeForm
        allowedExtensions={[tape.fileType]}
        onClose={() => {}}
        handleFile={(file) => uploadFile(file, tape)}
      />
    </Box>
  );
};

type VerificationUploadAccordionPanelProps = {
  selectedCreditFacility: Pick<
    CreditFacilityGQLType,
    'id' | 'slug' | 'formattedSlug' | 'loanProducts'
  >;
  selectedReportingDate: Util.FullDateStr;
  onUploadStart: () => void;
  onUploadComplete: (
    artifactDocumentId: string,
    creditFacilityId: string,
    reportingDate: Util.FullDateStr,
    fileVersion: number,
  ) => void;
};
export const VerificationUploadAccordionPanel = ({
  selectedCreditFacility,
  selectedReportingDate,
  onUploadStart,
  onUploadComplete,
}: VerificationUploadAccordionPanelProps) => {
  const [dataTapes, setDataTapes] = useState<DataTape[]>([]);
  const [sessionUploadSuccess, setSessionUploadSuccess] = useState(false);

  const toast = useToast();
  const { runInSandboxMode } = useSandboxMode();

  const {
    data: requiredDataTapesData,
  } = useGetRequiredDataTapesForVerificationQuery({
    variables: {
      creditFacilityId: selectedCreditFacility.id,
    },
    onCompleted(data) {
      const {
        dataTapes: extractedDataTapes,
      } = extractDataTapeMapFromRequiredDataTapes(data);
      setDataTapes(extractedDataTapes);
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (!requiredDataTapesData) {
    return (
      <Skeleton data-testid="loading-verification-uploader" height="600px" />
    );
  }
  const upload = uploadTapeForLenderVerification({
    creditFacilityId: selectedCreditFacility.id,
    selectedReportingDate,
    authToken: getAuthTokenOrThrow(),
    runInSandboxMode,
  });

  const uploadFile = async (
    file: File,
    tape: DataTape | undefined,
  ): Promise<void> => {
    if (!tape) return;

    onUploadStart();

    try {
      // upload the file provided.
      const { artifactDocumentId, fileVersion } = await upload({
        file,
        artifactId: tape.artifactId,
      });

      if (!sessionUploadSuccess) {
        setSessionUploadSuccess(true);
      }

      onUploadComplete(
        artifactDocumentId,
        selectedCreditFacility.id,
        selectedReportingDate,
        fileVersion,
      );
    } catch (err) {
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing upload. Please try again, and contact your Finley representative if this persists.',
      });
    }
  };

  return (
    <Box>
      {dataTapes.map((tape) => (
        <UploadForm key={tape.artifactId} tape={tape} uploadFile={uploadFile} />
      ))}
    </Box>
  );
};
